import {makeAutoObservable, observable} from 'mobx'
import React from 'react'
import {useContext} from 'react'
import {DataObjectId} from 'yougile-model'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {ChatSubscribersService} from '@components/chat/chat-subscribers-chooser/chat-subscribers-service'
import {TableUserInfo} from '@components/common/users-table/users-table'
import {getAllTasksFromPrivateLists} from '@components/my-tasks/utils'
import {ChatSubscribers} from '@extensions/chat-subscribers'
import {globals} from '@extensions/globals-base'
import {SubtaskUtil} from '@extensions/subtask-util'

interface State {
  open: boolean
  isPrivate: boolean
  isFromPrivateList: boolean
  chosen: DataObjectId[]
  canEditSubscribers?: 'yes' | 'no' | 'self'
}

const getInitialState = (): State => ({
  isPrivate: true,
  isFromPrivateList: false,
  chosen: [],
  open: false
})

export class ChatSubscribersChooserStore {
  state = getInitialState()
  private needToUpdateUsers = false
  usersAll: TableUserInfo[] = []

  constructor(
    private chatSubscribersService: ChatSubscribersService,
    canEditSubscribers: 'yes' | 'no' | 'self'
  ) {
    if (!chatSubscribersService.isTask || !this.hasTask) {
      return
    }
    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        usersAll: observable.ref
      },
      {autoBind: true}
    )
    this.state.canEditSubscribers = canEditSubscribers
    this.syncState()
  }

  syncState(): void {
    const task = this.chatSubscribersService.task
    this.state.isPrivate = !!task?.data.private
    if (task) {
      const parents = SubtaskUtil.getParentTaskIds(task.id)
      const allTasksFromPrivateLists = getAllTasksFromPrivateLists()
      this.state.isFromPrivateList = !parents.length
        ? allTasksFromPrivateLists.includes(task?.id)
        : parents.some(parentId => allTasksFromPrivateLists.includes(parentId))
    }
    this.state.chosen = this.chatSubscribersService.getSubscribedUsers()
    this.needToUpdateUsers = true
  }

  get users() {
    if (!this.usersAll || this.needToUpdateUsers) {
      this.usersAll = this.chatSubscribersService.getAllUsers()
      this.needToUpdateUsers = false
    }
    return this.usersAll
  }

  get hasTask(): boolean {
    return !!this.chatSubscribersService.task
  }

  get isSubscribed(): boolean {
    return (
      !!globals.currentUserId &&
      this.state.chosen.includes(globals.currentUserId)
    )
  }

  toggleOpen(): void {
    if (this.state.canEditSubscribers === 'no') {
      return
    }
    this.state.open = !this.state.open
  }

  setSubscription(uid: DataObjectId, isSubscribed: boolean): void {
    if (isSubscribed) {
      this.state.chosen.push(uid)
    } else {
      this.state.chosen = this.state.chosen.filter(uId => uId !== uid)
    }
    ChatSubscribers.setSubscription(
      uid,
      this.chatSubscribersService.task,
      isSubscribed
    )
  }

  toggleSubscriber(userIds: DataObjectId[]): void {
    SyncCommitHandler.doInBatch(() => {
      userIds.forEach(uid =>
        this.setSubscription(uid, !this.state.chosen.includes(uid))
      )
    })
  }

  makePublic(): void {
    this.state.isPrivate = false
    this.chatSubscribersService.makePublic()
  }
}

export const StoreContext = React.createContext(
  undefined as unknown as ChatSubscribersChooserStore
)

export const useStore = () => {
  const store = useContext(StoreContext)
  return [store.state, store] as const
}
